// Emergency Numbers → the raw records behind a domain's verdict.
import { useState } from "react";
import { CheckCheck, Loader2, Trash2, Undo2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ADDRESS_STATE, BAD_STATES, NUMBER_STATE, Pill, ago, fmtTn } from "./ui";

const addrText = (r) => [r["address-line-1"], r["address-line-2"], r["address-city"], r["address-state-province-abbreviation"],
  r["address-postal-code"]].filter(Boolean).join(" ") || "—";

function BwCell({ bw }) {
  if (!bw) return <span className="text-slate-300">—</span>;
  if (bw.status === "unconfigured") return <Pill tone="bg-slate-200 text-slate-600" title="Bandwidth is not connected">unknown</Pill>;
  if (bw.status === "error") return <Pill tone="bg-rose-100 text-rose-800">lookup failed</Pill>;
  if (bw.provisioned) return <span className="text-[12px] text-emerald-800" title={bw.address}>static · {bw.address || "provisioned"}</span>;
  return <Pill tone="bg-amber-100 text-amber-800" title={bw.status}>{bw.status === "absent" ? "no static" : bw.status || "not usable"}</Pill>;
}

function Section({ title, count, children, testid, actions, forceOpen = false }) {
  return (
    <details className="rounded-md border border-slate-200 bg-white" data-testid={testid} open={forceOpen || (!!count && count <= 8)}>
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600">
        <span className="flex-1">{title} <span className="font-normal text-slate-400">({count})</span></span>
        {actions && <span onClick={(e) => e.stopPropagation()}>{actions}</span>}
      </summary>
      <div className="overflow-x-auto border-t border-slate-100">{children}</div>
    </details>
  );
}

const TH = ({ children }) => <th className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{children}</th>;
const TD = ({ children, mono, className = "" }) => (
  <td className={`px-3 py-1.5 text-[12px] text-slate-700 ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
);

// A not-registered user's emergency number/address, corrected straight on the Portal —
// the two dropdowns are sourced from this same domain's Emergency Endpoints + Addresses,
// so only a value the Portal already knows about can be picked. With u.user === "domain"
// it sets the domain default every subscriber without an address of their own falls back to.
function FixUserButton({ u, endpoints, addresses, onFixUser, label = "Fix", title }) {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [addressId, setAddressId] = useState("");
  const [busy, setBusy] = useState(false);
  const isDefault = u.user === "domain";

  const numberOptions = endpoints.map((r) => String(r["emergency-address-id"] || "")).filter(Boolean);
  const addressOptions = addresses.map((r) => String(r["emergency-address-id"] || "")).filter((v) => v.startsWith("a-"));

  const apply = async () => {
    if (!number || !addressId) return;
    setBusy(true);
    const ok = await onFixUser(u.user, number, addressId);
    setBusy(false);
    if (ok) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => {
      setOpen(o);
      if (o) { setNumber(numberOptions[0] || ""); setAddressId(addressOptions[0] || ""); }
    }}>
      <PopoverTrigger asChild>
        <Button size="sm" variant={isDefault ? "default" : "outline"} className="h-6 gap-1 px-2 text-[11px]" title={title}
                data-testid={`e911-fix-user-${u.user}`}>
          <Wrench className="h-3 w-3" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2 p-3" data-testid={`e911-fix-user-popover-${u.user}`}>
        <div className="text-xs font-medium text-slate-600">
          {isDefault ? "Set the domain default emergency caller-id" : `Fix ${u.user}'s emergency caller-id`}
        </div>
        {isDefault && (
          <p className="text-[11px] text-slate-500">
            Written to the <span className="font-mono">domain</span> subscriber on the Portal. Every user without an
            address of their own inherits it.
          </p>
        )}
        <div className="space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Emergency number</label>
          <Select value={number} onValueChange={setNumber}>
            <SelectTrigger className="h-8 text-xs" data-testid={`e911-fix-number-${u.user}`}>
              <SelectValue placeholder="Pick a number" />
            </SelectTrigger>
            <SelectContent>
              {numberOptions.map((tn) => <SelectItem key={tn} value={tn}>{fmtTn(tn)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Emergency address</label>
          <Select value={addressId} onValueChange={setAddressId}>
            <SelectTrigger className="h-8 text-xs" data-testid={`e911-fix-address-${u.user}`}>
              <SelectValue placeholder="Pick an address" />
            </SelectTrigger>
            <SelectContent>
              {addresses.map((r) => {
                const id = String(r["emergency-address-id"] || "");
                if (!id.startsWith("a-")) return null;
                return <SelectItem key={id} value={id}>{id} — {r["address-name"] || r["caller-name"] || "address"}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="h-7 w-full gap-1 text-xs" onClick={apply} disabled={busy || !number || !addressId}
                data-testid={`e911-fix-apply-${u.user}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
          {isDefault ? "Set the domain default" : "Apply to the Portal"}
        </Button>
        {(!numberOptions.length || !addressOptions.length) && (
          <p className="text-[10px] text-amber-600">
            This domain has no registered Emergency Endpoint/Address to pick from yet.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Clears a whole domain's backlog in one go — same two dropdowns, applied to every
// currently-mismatched user at once via the bulk endpoint.
function FixAllButton({ users, endpoints, addresses, onFixAllUsers }) {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [addressId, setAddressId] = useState("");
  const [busy, setBusy] = useState(false);

  const numberOptions = endpoints.map((r) => String(r["emergency-address-id"] || "")).filter(Boolean);
  const addressOptions = addresses.map((r) => String(r["emergency-address-id"] || "")).filter((v) => v.startsWith("a-"));

  if (!users.length) return null;

  const apply = async () => {
    if (!number || !addressId) return;
    setBusy(true);
    const ok = await onFixAllUsers(users.map((u) => u.user), number, addressId);
    setBusy(false);
    if (ok) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => {
      setOpen(o);
      if (o) { setNumber(numberOptions[0] || ""); setAddressId(addressOptions[0] || ""); }
    }}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px]" data-testid="e911-fix-all-users">
          <Wrench className="h-3 w-3" /> Fix all ({users.length})
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-2 p-3" data-testid="e911-fix-all-popover">
        <div className="text-xs font-medium text-slate-600">Fix {users.length} mismatched user{users.length === 1 ? "" : "s"}</div>
        <p className="text-[11px] text-slate-500">Applies the same emergency number + address to every flagged user below.</p>
        <div className="space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Emergency number</label>
          <Select value={number} onValueChange={setNumber}>
            <SelectTrigger className="h-8 text-xs" data-testid="e911-fix-all-number">
              <SelectValue placeholder="Pick a number" />
            </SelectTrigger>
            <SelectContent>
              {numberOptions.map((tn) => <SelectItem key={tn} value={tn}>{fmtTn(tn)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Emergency address</label>
          <Select value={addressId} onValueChange={setAddressId}>
            <SelectTrigger className="h-8 text-xs" data-testid="e911-fix-all-address">
              <SelectValue placeholder="Pick an address" />
            </SelectTrigger>
            <SelectContent>
              {addresses.map((r) => {
                const id = String(r["emergency-address-id"] || "");
                if (!id.startsWith("a-")) return null;
                return <SelectItem key={id} value={id}>{id} — {r["address-name"] || r["caller-name"] || "address"}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="h-7 w-full gap-1 text-xs" onClick={apply} disabled={busy || !number || !addressId}
                data-testid="e911-fix-all-apply">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />} Apply to the Portal ({users.length})
        </Button>
        {(!numberOptions.length || !addressOptions.length) && (
          <p className="text-[10px] text-amber-600">
            This domain has no registered Emergency Endpoint/Address to pick from yet.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

// "This different address is intended" — a manager's mark that takes the user(s) off the ≠ Endpoint
// list for good (until the pair in force changes). Reason is optional but kept with the mark.
function IntendedButton({ users, onMarkIntended, label, testKey }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    const ok = await onMarkIntended(users, reason, true);
    setBusy(false);
    if (ok) { setOpen(false); setReason(""); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px]" data-testid={`e911-intended-${testKey}`}
                title="Mark this different address as intended (e.g. a branch office sharing the head-office number) — drops it off the ≠ Endpoint list">
          <CheckCheck className="h-3 w-3" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2 p-3" data-testid={`e911-intended-popover-${testKey}`}>
        <div className="text-xs font-medium text-slate-600">
          Mark {users.length === 1 ? (users[0] === "domain" ? "the domain default pair" : `user ${users[0]}`) : `${users.length} users`} as intended
        </div>
        <p className="text-[11px] text-slate-500">
          The user keeps their own address; it simply stops being flagged. If the address or number changes later, it is flagged again.
        </p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300}
                  placeholder="Why it is intended (optional) — e.g. branch office, head-office number"
                  className="text-xs" data-testid={`e911-intended-reason-${testKey}`} />
        <Button size="sm" className="h-7 w-full gap-1 text-xs" onClick={apply} disabled={busy} data-testid={`e911-intended-apply-${testKey}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />} Mark as intended
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function ReflagButton({ user, onMarkIntended }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px] text-slate-500" disabled={busy}
            title="Remove the manager's mark so this pair is flagged again"
            onClick={async () => { setBusy(true); await onMarkIntended([user], "", false); setBusy(false); }}
            data-testid={`e911-reflag-${user}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Re-flag
    </Button>
  );
}

function IntendedPill({ ack, testid }) {
  return (
    <Pill tone="bg-slate-100 text-slate-600" testid={testid}
          title={`Marked intended by ${ack.by || "a manager"} ${ack.at ? ago(ack.at) : ""}${ack.reason ? ` — ${ack.reason}` : ""}`}>
      intended{ack.reason ? ` · ${ack.reason.length > 28 ? ack.reason.slice(0, 28) + "…" : ack.reason}` : ""}
    </Pill>
  );
}

// How one subscriber's address / number resolves — from the assessor when the domain was
// assessed with the subscriber check, otherwise derived from the records at hand.
function StateCell({ u, legacyBad }) {
  if (!u.address_state) {
    return legacyBad ? <Pill tone="bg-rose-100 text-rose-800">not registered</Pill> : <span className="text-slate-300">—</span>;
  }
  const a = ADDRESS_STATE[u.address_state] || { label: u.address_state, tone: "bg-slate-100 text-slate-600" };
  const n = NUMBER_STATE[u.number_state];
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Pill tone={a.tone} testid={`e911-sub-address-state-${u.user}`}>{a.label}</Pill>
      {n && BAD_STATES.includes(u.number_state) && <Pill tone={n.tone} testid={`e911-sub-number-state-${u.user}`}>{n.label}</Pill>}
      {u.address_match === false && !u.mismatch_ack && (
        <Pill tone="bg-amber-100 text-amber-800" testid={`e911-sub-mismatch-${u.user}`}
              title={`Address ${u.address_text || ""} ≠ what Endpoint ${u.endpoint_number || ""} carries: ${u.endpoint_text || ""}`}>
          ≠ endpoint address
        </Pill>
      )}
      {u.address_match === false && u.mismatch_ack && <IntendedPill ack={u.mismatch_ack} testid={`e911-sub-intended-${u.user}`} />}
    </span>
  );
}

export default function DomainRecords({ doc, canEdit, onFixUser, onFixAllUsers, onMarkIntended, onDeleteEndpoint }) {
  const bw = doc.bandwidth || {};
  const addresses = doc.addresses || [];
  const endpoints = doc.endpoints || [];
  const legacy = doc.legacy_numbers || [];
  const users = doc.emergency_users || [];
  const userAddr = doc.user_addresses || [];
  const cfg = doc.ui_configs || {};
  const inherited = cfg._inherited || {};
  const configNames = Object.keys(cfg).filter((k) => k !== "_inherited");
  const dd = doc.domain_default;
  const isDlr = doc.expected_mode === "DLR";
  const validNumbers = new Set(endpoints.map((r) => String(r["emergency-address-id"] || "")).filter(Boolean));
  const validAddressIds = new Set(addresses.map((r) => String(r["emergency-address-id"] || "")).filter((v) => v.startsWith("a-")));
  const legacyBad = (u) => !validNumbers.has(u.number) || !validAddressIds.has(u.address_id);
  const isBad = (u) => (u.address_state
    ? BAD_STATES.includes(u.address_state) || BAD_STATES.includes(u.number_state)
    : legacyBad(u));
  const isMism = (u) => u.address_match === false && !u.mismatch_ack;
  const isAcked = (u) => u.address_match === false && !!u.mismatch_ack;
  const mismatched = users.filter((u) => isBad(u) && (u.regular ?? true));
  const flaggedIds = users.filter((u) => isMism(u) && (u.regular ?? true)).map((u) => String(u.user));
  const defaultBad = dd && (BAD_STATES.includes(dd.address_state) || BAD_STATES.includes(dd.number_state));
  const subsTitle = doc.subscribers?.regular != null
    ? `Subscribers · ${doc.subscribers.regular} real user${doc.subscribers.regular === 1 ? "" : "s"}`
    : "Users with an emergency caller-id";

  return (
    <div className="space-y-2" data-testid="e911-records">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Records on the Portal</div>

      <Section title="Emergency Endpoints (DLR)" count={endpoints.length} testid="e911-rec-endpoints">
        <table className="w-full text-left">
          <thead><tr><TH>Number</TH><TH>Name</TH><TH>Address</TH><TH>Users</TH><TH>Bandwidth static</TH>{canEdit && <TH />}</tr></thead>
          <tbody>
            {endpoints.map((r, i) => {
              const tn = String(r["emergency-address-id"] || "");
              const onIt = users.filter((u) => u.number === tn).length;
              const isDefault = dd?.number === tn;
              return (
                <tr key={i} className="border-t border-slate-100">
                  <TD mono>{fmtTn(tn)}</TD>
                  <TD>{r["caller-name"] || r["address-name"] || "—"}</TD>
                  <TD>{addrText(r)}</TD>
                  <TD>
                    <span className={onIt || isDefault ? "" : "text-amber-700 font-medium"} data-testid={`e911-endpoint-users-${tn}`}>
                      {onIt}{isDefault ? " · domain default" : ""}
                    </span>
                  </TD>
                  <TD><BwCell bw={bw[tn]} /></TD>
                  {canEdit && (
                    <TD>
                      {!onIt && !isDefault && onDeleteEndpoint && (
                        <DeleteEndpointButton tn={tn} row={r} bw={bw[tn]} addresses={addresses} onConfirm={onDeleteEndpoint} />
                      )}
                    </TD>
                  )}
                </tr>
              );
            })}
            {!endpoints.length && <tr><TD className="text-slate-400">None</TD></tr>}
          </tbody>
        </table>
      </Section>

      <Section title="Emergency Addresses (domain)" count={addresses.length} testid="e911-rec-addresses">
        <table className="w-full text-left">
          <thead><tr><TH>Id</TH><TH>Name</TH><TH>Caller name</TH><TH>Address</TH><TH>Callback</TH></tr></thead>
          <tbody>
            {addresses.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <TD mono>{r["emergency-address-id"] || "—"}</TD>
                <TD>{r["address-name"] || "—"}</TD>
                <TD>{r["caller-name"] || "—"}</TD>
                <TD>{addrText(r)}</TD>
                <TD mono>{r["address-callback-number"] ? fmtTn(r["address-callback-number"]) : "—"}</TD>
              </tr>
            ))}
            {!addresses.length && <tr><TD className="text-slate-400">None</TD></tr>}
          </tbody>
        </table>
      </Section>

      {!!userAddr.length && (
        <Section title="Emergency Addresses pinned to users" count={userAddr.length} testid="e911-rec-user-addresses">
          <table className="w-full text-left">
            <thead><tr><TH>User</TH><TH>Id</TH><TH>Address</TH></tr></thead>
            <tbody>
              {userAddr.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <TD mono>{r._user}</TD><TD mono>{r["emergency-address-id"]}</TD><TD>{addrText(r)}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      <Section title="Emergency Numbers (Legacy · static)" count={legacy.length} testid="e911-rec-legacy">
        <table className="w-full text-left">
          <thead><tr><TH>Number</TH><TH>Stored as</TH><TH>Tags</TH><TH>Bandwidth static</TH></tr></thead>
          <tbody>
            {legacy.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <TD mono>{fmtTn(r.tn)}</TD>
                <TD mono>{(r.callids || []).join(", ") || "—"}</TD>
                <TD>{(r.tags || []).join(", ") || "—"}</TD>
                <TD><BwCell bw={bw[r.tn]} /></TD>
              </tr>
            ))}
            {!legacy.length && <tr><TD className="text-slate-400">None</TD></tr>}
          </tbody>
        </table>
      </Section>

      <Section title={subsTitle} count={users.length} testid="e911-rec-users"
               forceOpen={!!mismatched.length || (isDlr && !!defaultBad) || users.some(isMism)}
                actions={canEdit && (!!mismatched.length || flaggedIds.length > 1) && (
                  <span className="inline-flex items-center gap-1">
                    {!!mismatched.length && (
                      <FixAllButton users={mismatched} endpoints={endpoints} addresses={addresses} onFixAllUsers={onFixAllUsers} />
                    )}
                    {flaggedIds.length > 1 && (
                      <IntendedButton users={flaggedIds} onMarkIntended={onMarkIntended} label={`Mark all ${flaggedIds.length} intended`} testKey="all" />
                    )}
                  </span>
                )}>
        <table className="w-full text-left">
          <thead><tr><TH>User</TH><TH>Name</TH><TH>Type</TH><TH>Emergency number</TH><TH>Address id</TH><TH>Resolves to</TH><TH>Fix</TH></tr></thead>
          <tbody>
            {dd && (
              <tr className={`border-t border-slate-100 ${defaultBad && isDlr ? "bg-orange-50/70" : "bg-slate-50/70"}`} data-testid="e911-sub-domain-default">
                <TD mono>domain</TD>
                <TD><span className="font-medium text-slate-700" title="The 'domain' subscriber — fallback for every user without an address of their own">Domain default</span></TD>
                <TD><span className="text-slate-400">domain</span></TD>
                <TD mono className={BAD_STATES.includes(dd.number_state) ? "text-rose-700" : ""}>{dd.number ? fmtTn(dd.number) : "—"}</TD>
                <TD mono className={BAD_STATES.includes(dd.address_state) ? "text-rose-700" : ""}>{dd.address_id || "—"}</TD>
                <TD>
                  {dd.address_state ? (
                    <span className="inline-flex flex-wrap gap-1">
                      <Pill tone={(ADDRESS_STATE[dd.address_state] || {}).tone} testid="e911-sub-address-state-domain">
                        {dd.address_state === "ok" ? "address set" : dd.address_state === "none" ? "not set" : "unknown id"}
                      </Pill>
                      {BAD_STATES.includes(dd.number_state) && <Pill tone={NUMBER_STATE[dd.number_state].tone}>{NUMBER_STATE[dd.number_state].label}</Pill>}
                      {dd.address_match === false && !dd.mismatch_ack && (
                        <Pill tone="bg-amber-100 text-amber-800" testid="e911-sub-mismatch-domain"
                              title={`${dd.address_text || ""} ≠ what Endpoint ${dd.number} carries: ${dd.endpoint_text || ""}`}>≠ endpoint address</Pill>
                      )}
                      {dd.address_match === false && dd.mismatch_ack && <IntendedPill ack={dd.mismatch_ack} testid="e911-sub-intended-domain" />}
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </TD>
                <TD>
                  {!isDlr ? <span className="text-slate-300">—</span>
                   : canEdit ? (
                     <span className="inline-flex items-center gap-1">
                       <FixUserButton u={{ user: "domain" }} endpoints={endpoints} addresses={addresses} onFixUser={onFixUser}
                                      label={defaultBad ? "Set the domain default" : "Change"}
                                      title="Write the chosen Emergency Endpoint number + Emergency Address to the domain subscriber on the Portal" />
                       {dd.address_match === false && !dd.mismatch_ack && <IntendedButton users={["domain"]} onMarkIntended={onMarkIntended} label="Intended" testKey="domain" />}
                       {dd.address_match === false && dd.mismatch_ack && <ReflagButton user="domain" onMarkIntended={onMarkIntended} />}
                     </span>
                   )
                   : defaultBad ? <Pill tone="bg-orange-100 text-orange-800">not set</Pill> : <span className="text-slate-300">—</span>}
                </TD>
              </tr>
            )}
            {users.map((u, i) => {
              const bad = isBad(u);
              const mism = isMism(u);
              const acked = isAcked(u);
              const matters = u.regular ?? true;
              const ownPair = !!(u.address_id || u.number);  // an inherited pair is marked via the domain default row
              return (
                <tr key={i} className={`border-t border-slate-100 ${bad && matters ? "bg-rose-50/50" : mism && matters ? "bg-amber-50/50" : ""}`} data-testid={`e911-sub-row-${u.user}`}>
                  <TD mono>{u.user}</TD><TD>{u.name || "—"}</TD>
                  <TD><span className="text-[11px] text-slate-500">{u.regular === false ? (u.service_code || "system") : "user"}</span></TD>
                  <TD mono className={bad && matters ? "text-rose-700" : ""}>{u.number ? fmtTn(u.number) : <span className="text-slate-400">—</span>}</TD>
                  <TD mono className={bad && matters ? "text-rose-700" : mism && matters ? "text-amber-700" : ""}>{u.address_id || <span className="text-slate-400">—</span>}</TD>
                  <TD><StateCell u={u} legacyBad={legacyBad(u)} /></TD>
                  <TD>
                    {!bad && !mism && !acked ? <span className="text-slate-300">—</span>
                     : canEdit ? (
                       <span className="inline-flex items-center gap-1">
                         {(bad || mism) && (
                           <FixUserButton u={u} endpoints={endpoints} addresses={addresses} onFixUser={onFixUser}
                                          label={bad ? "Fix" : "Repoint"}
                                          title={bad ? undefined : "Point this user at the Endpoint's own address — only if the different address is not intended"} />
                         )}
                         {mism && ownPair && <IntendedButton users={[String(u.user)]} onMarkIntended={onMarkIntended} label="Intended" testKey={u.user} />}
                         {acked && ownPair && <ReflagButton user={String(u.user)} onMarkIntended={onMarkIntended} />}
                       </span>
                     )
                     : bad ? <Pill tone="bg-rose-100 text-rose-800">not registered</Pill> : <span className="text-slate-300">—</span>}
                  </TD>
                </tr>
              );
            })}
            {!users.length && !dd && <tr><TD className="text-slate-400">None</TD></tr>}
          </tbody>
        </table>
        {!!users.some((u) => u.regular === false) && (
          <p className="px-3 py-1.5 text-[10px] text-slate-400">
            Queues, attendants and TODs are listed for completeness — only real users and extensions decide the verdict.
          </p>
        )}
      </Section>

      {!!configNames.length && (
        <Section title="Portal UI configs" count={configNames.length} testid="e911-rec-configs">
          <table className="w-full text-left">
            <thead><tr><TH>Config</TH><TH>Domain level</TH><TH>Inherited</TH></tr></thead>
            <tbody>
              {configNames.map((k) => (
                <tr key={k} className="border-t border-slate-100">
                  <TD mono>{k}</TD>
                  <TD mono>{cfg[k] == null ? <span className="text-slate-400">not set</span> : cfg[k]}</TD>
                  <TD mono>{inherited[k] ? `${inherited[k].value} (from ${inherited[k].reseller !== "*" ? inherited[k].reseller : inherited[k].domain})` : "—"}</TD>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}

// Iter 311 — remove an endpoint nobody uses (and its address when nothing else points at it).
function DeleteEndpointButton({ tn, row, bw, addresses, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const match = addresses.find((a) => (a["caller-name"] || a["address-name"]) && (a["caller-name"] || a["address-name"]) === (row["caller-name"] || row["address-name"])
    && String(a["address-line-1"] || "").toUpperCase().startsWith(String(row["address-line-1"] || "").toUpperCase().split(" ")[0]));
  const go = async () => {
    setBusy(true);
    try { const ok = await onConfirm(tn); if (ok) setOpen(false); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600" title="Delete this unused endpoint (and its address)"
              onClick={() => setOpen(true)} data-testid={`e911-endpoint-delete-${tn}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent data-testid="e911-endpoint-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete endpoint {fmtTn(tn)} from the Portal?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <div>No subscriber uses this number as their emergency caller-id, so nothing will lose its 911 address.</div>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Emergency Endpoint <b>{fmtTn(tn)}</b> — {row["caller-name"] || row["address-name"] || "unnamed"}, {addrText(row)}</li>
                  <li>{match ? <>Emergency Address <b>{match["emergency-address-id"]}</b> ({match["address-name"] || match["caller-name"]}) — removed only if no user, other endpoint or the domain default points at it</> : "Its matching Emergency Address, if one exists and nothing else uses it"}</li>
                  <li>{bw?.provisioned ? "Bandwidth still holds a static registration for this number; that is left untouched." : "Bandwidth's static side has nothing for this number, so there is nothing to clean up there."}</li>
                </ul>
                <div>The Portal is re-checked live before deleting; if a user has picked this number since the last assessment, the delete is refused. The domain is re-assessed afterwards.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="e911-endpoint-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} className="bg-rose-600 hover:bg-rose-700"
                               data-testid="e911-endpoint-delete-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete endpoint
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
